import { AxiosError, AxiosResponse } from 'axios';
import { useQuery, UseQueryResult } from 'react-query';

import { QuizAPI } from 'services/quiz.api';
import { IQuiz, IQuizLatestEntry } from './quiz.types';

/**
 * Query keys for the quiz-related calls. Centralised so cache invalidation
 * (e.g. after submitting an entry in the next ticket) can target them
 * unambiguously.
 */
export const quizQueryKeys = {
  user: (quizId: number) => ['quiz', quizId, 'user'] as const,
  latestEntry: (quizId: number) => ['quiz', quizId, 'latestEntry'] as const,
};

/**
 * Loads the quiz with the `form:user` serialization group.
 * The endpoint returns 403 when the user is not targeted by the operation;
 * we surface the AxiosError as-is so the page can branch on `response.status`.
 */
export const useUserQuiz = (
  quizId: number,
): UseQueryResult<IQuiz, AxiosError> =>
  useQuery<IQuiz, AxiosError>(
    quizQueryKeys.user(quizId),
    () =>
      QuizAPI.getUserQuiz(quizId).then(
        (response: AxiosResponse<IQuiz>) => response.data,
      ),
    {
      enabled: Number.isFinite(quizId),
      // A 403 is a deterministic answer ("not targeted"): retrying would be
      // pointless and noisy in the network tab.
      retry: (failureCount, error) => {
        if (error?.response?.status === 403) return false;
        return failureCount < 1;
      },
    },
  );

/**
 * Loads the latest entry submitted by the current user for this quiz.
 * The endpoint answers with 204 No Content when the user has not submitted
 * yet, in which case the hook resolves to `null`.
 */
export const useUserQuizLatestEntry = (
  quizId: number,
): UseQueryResult<IQuizLatestEntry | null, AxiosError> =>
  useQuery<IQuizLatestEntry | null, AxiosError>(
    quizQueryKeys.latestEntry(quizId),
    () =>
      QuizAPI.getLatestUserQuizEntry(quizId).then(
        (response: AxiosResponse<IQuizLatestEntry | '' | null>) => {
          // Axios exposes 204 as `status === 204` with an empty body. We
          // normalise both the empty string and `null` to a single `null`
          // sentinel value consumed by the page.
          if (response.status === 204 || !response.data) return null;
          return response.data as IQuizLatestEntry;
        },
      ),
    {
      enabled: Number.isFinite(quizId),
    },
  );
